import { Box } from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import { FC, useDeferredValue, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAllExperiences } from "../hooks/useAllExperiences";
import { useIsMobile } from "../hooks/useIsMobile";
import { useLocalization } from "../hooks/useLocalization";
import { useIncomingLicenses } from "../pages/experiences/hooks/useIncomingLicenses";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { ExpirationDatePicker } from "./ExpirationDatePicker";
import { SearchBar } from "./SearchBar";
import { SelectableList, SelectableListItem } from "./SelectableList";

interface GrantAccessDialogProps {
  isOpen: boolean;
  onClose: () => void;
  hasAccessContentMetadataIds?: string[];
  hasAccessText?: string;
  onGrantAccess?: ({
    releaseChannelId,
    expirationDate,
  }: {
    releaseChannelId: string;
    expirationDate?: Date;
  }) => Promise<void>;
  onGrantAllocation?: ({
    releaseChannelId,
    expirationDate,
  }: {
    releaseChannelId: string;
    expirationDate?: Date;
  }) => Promise<void>;
  isSubmitLoading?: boolean;
}

export const GrantAccessDialog: FC<GrantAccessDialogProps> = ({
  isOpen,
  onClose,
  hasAccessContentMetadataIds = [],
  hasAccessText,
  onGrantAccess,
  onGrantAllocation,
  isSubmitLoading = false,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [selectedReleaseChannelId, setSelectedReleaseChannelId] = useState<
    string | null
  >(null);
  const [checked, setChecked] = useState(true);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);

  const { experiences } = useAllExperiences();
  const { licensesByReleaseChannel } = useIncomingLicenses();
  const { companyId } = useParams();
  const isMobile = useIsMobile();

  const mappedItems = useMemo(() => {
    const hasAccessMap = hasAccessContentMetadataIds.reduce<
      Record<string, boolean>
    >((acc, id) => {
      acc[id] = true;

      return acc;
    }, {});

    const sortedExperiences = [...experiences].sort((a, b) => {
      return a.friendlyName.localeCompare(b.friendlyName);
    });

    return sortedExperiences.map<SelectableListItem>((experience) => {
      const hasAccess = !!hasAccessMap[experience.contentMetadataId];
      const isExternal =
        experience.companyId !== companyId &&
        licensesByReleaseChannel.has(experience.releaseChannelId);
      const license = isExternal
        ? licensesByReleaseChannel.get(experience.releaseChannelId)
        : null;

      const hasSeatsLimit = license?.seats;
      const seatsAvailable =
        hasSeatsLimit &&
        license.seats &&
        license.seats - license.allocatedSeats > 0;

      const hasWhitelist = license?.hasWhitelist;

      let tertiaryText: string | undefined = undefined;
      let type: SelectableListItem["type"] = "selectable";

      if (hasAccess) {
        tertiaryText =
          hasAccessText ?? t("components.grantAccessDialog.hasAccess");
        type = "hasAccess";
      } else if (isExternal && hasWhitelist) {
        tertiaryText = t("components.grantAccessDialog.hasWhitelist");
        type = "disabled";
      } else if (isExternal && hasSeatsLimit) {
        tertiaryText = `${license.allocatedSeats}/${license.seats} seats`;
        if (!seatsAvailable) {
          type = "disabled";
        }
      } else if (isExternal && !hasSeatsLimit) {
        tertiaryText = undefined;
      }

      return {
        id: experience.releaseChannelId,
        primaryText: experience.friendlyName,
        secondaryText: experience.companyName ?? "-",
        tertiaryText,
        type,
      };
    });
  }, [
    hasAccessContentMetadataIds,
    experiences,
    companyId,
    licensesByReleaseChannel,
    hasAccessText,
    t,
  ]);

  const selectableItems = useMemo(() => {
    return deferredSearchTerm
      ? mappedItems.filter((item) =>
        `${item.primaryText} ${item.secondaryText}`
          .toLowerCase()
          .includes(deferredSearchTerm.toLowerCase()),
      )
      : mappedItems;
  }, [mappedItems, deferredSearchTerm]);

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
  };

  const handleSearchClear = () => {
    setSearchTerm("");
  };

  const handleReleaseChannelSelect = (id: string) => {
    setSelectedReleaseChannelId(id);
  };

  const handleSubmit = async () => {
    if (selectedReleaseChannelId) {
      const expirationDateToUse =
        checked || !expirationDate ? undefined : expirationDate;

      if (isExternalExperience && onGrantAllocation) {
        await onGrantAllocation({
          releaseChannelId: selectedReleaseChannelId,
          expirationDate: expirationDateToUse,
        });
      } else if (onGrantAccess) {
        await onGrantAccess({
          releaseChannelId: selectedReleaseChannelId,
          expirationDate: expirationDateToUse,
        });
      }
      setSelectedReleaseChannelId(null);
      handleSearchClear();
      setExpirationDate(null);
      setChecked(true);
      onClose();
    }
  };

  const handleUnselect = () => {
    setSelectedReleaseChannelId(null);
    setExpirationDate(null);
  };

  const handleCancel = () => {
    setSelectedReleaseChannelId(null);
    handleSearchClear();
    setExpirationDate(null);
    setChecked(true);
    onClose();
  };

  const actionText = useMemo(
    () =>
      isMobile
        ? t("components.grantAccessDialog.grant")
        : t("components.grantAccessDialog.grantAccess"),
    [isMobile, t],
  );

  const isExternalExperience = useMemo(() => {
    const selectedExperience = experiences.find(
      (exp) => exp.releaseChannelId === selectedReleaseChannelId,
    );

    return selectedExperience?.companyId !== companyId;
  }, [companyId, experiences, selectedReleaseChannelId]);

  const selectedItems = useMemo(() => {
    const selectedItem = mappedItems.find(
      (item) => item.id === selectedReleaseChannelId,
    );

    return selectedItem ? [selectedItem] : [];
  }, [mappedItems, selectedReleaseChannelId]);

  const isGrantDisabled =
    !selectedReleaseChannelId ||
    isSubmitLoading ||
    (!checked && !expirationDate);

  const maxDate = useMemo(() => {
    const lic = licensesByReleaseChannel.get(selectedReleaseChannelId ?? "");

    return lic?.expiresAt ?? null;
  }, [licensesByReleaseChannel, selectedReleaseChannelId]);

  return (
    <ConfirmationDialog.Root
      aria-describedby="grant-access-description"
      aria-labelledby="grant-access-title"
      fullScreenOnMobile={true}
      fullWidth
      height="728px"
      maxWidth="xs"
      onClose={handleCancel}
      open={isOpen}
    >
      <ConfirmationDialog.Title
        id="grant-access-title"
        showIcon={false}
        sx={{ fontSize: { xs: 18, md: 24 } }}
      >
        {t("components.grantAccessDialog.title")}
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content
        sx={{
          fontWeight: 500,
          color: theme.colors.text[1],
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <form onSubmit={handleSearchSubmit} style={{ width: "100%" }}>
          <SearchBar
            handleClear={handleSearchClear}
            handleSubmit={handleSearchSubmit}
            onChange={handleSearchChange}
            placeholder={t("components.grantAccessDialog.searchPlaceholder")}
            sx={{ maxWidth: undefined }}
            value={searchTerm}
          />
        </form>
        <SelectableList
          emptyMessage={t(
            "components.grantAccessDialog.noExperiencesAvailable",
          )}
          height="100%"
          items={selectableItems}
          maxHeight="100%"
          minHeight={0}
          multiSelect={false}
          onChipRemove={handleUnselect}
          onItemSelect={handleReleaseChannelSelect}
          selectedId={selectedReleaseChannelId}
          selectedItems={selectedItems}
          selectedItemsTitle="Selected Experience"
          sx={{ flex: 1, minHeight: 0 }}
        />
        <Box
          sx={{ visibility: selectedReleaseChannelId ? "visible" : "hidden" }}
        >
          <ExpirationDatePicker
            checked={checked}
            isExternal={isExternalExperience}
            key={selectedReleaseChannelId ? "active" : "inactive"}
            maxDate={maxDate ?? undefined}
            onChange={setExpirationDate}
            setChecked={setChecked}
            value={expirationDate}
          />
        </Box>
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions>
        <ConfirmationDialog.Button
          onClick={handleCancel}
          sx={{
            color: theme.colors.primary[5],
          }}
          variant="secondary"
        >
          {t("components.grantAccessDialog.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          disabled={isGrantDisabled}
          isLoading={isSubmitLoading}
          loadingText="Granting..."
          onClick={() => void handleSubmit()}
          variant="contained"
        >
          {actionText}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};
